/**
 * How a bill is named to people.
 *
 * Every bill carries an invoice_number, numbered per school: it is what the
 * downloaded PDF prints, what the duplicate warning quotes and what a bursar
 * reads out over the phone. The database id is none of those things, yet most
 * screens titled bills "Bill #412" with it, so one bill could show three
 * different numbers between the page, the file and the paper.
 */

interface BillNumberLike {
  id: string | number;
  invoice_number?: number | string | null;
}

interface BillPeriodLike {
  session?: { name?: string | null } | null;
  academicCalendar?: { termStructure?: { name?: string | null } | null } | null;
}

function invoiceDigits(invoiceNumber: number | string | null | undefined): string {
  if (invoiceNumber === null || invoiceNumber === undefined) return '';
  const digits = String(invoiceNumber).replace(/\D/g, '');
  return digits && Number(digits) > 0 ? digits : '';
}

/** "INV-000007" — the same shape the PDF and the backend's own messages use. */
export function formatInvoiceNumber(invoiceNumber: number | string): string {
  return `INV-${invoiceDigits(invoiceNumber).padStart(6, '0')}`;
}

/** True when the bill has been given an invoice number. */
export function hasInvoiceNumber(bill: BillNumberLike): boolean {
  return invoiceDigits(bill.invoice_number) !== '';
}

/**
 * The bill's name: its invoice number when it has one, otherwise the id with
 * no pretence ("Bill #412") so a row without a number still reads as a bill.
 */
export function billNumberLabel(bill: BillNumberLike): string {
  return hasInvoiceNumber(bill) ? formatInvoiceNumber(bill.invoice_number!) : `Bill #${bill.id}`;
}

/** A filename-safe stem: "INV-000007", or "bill-412" for a bill with no invoice number. */
export function billFileStem(bill: BillNumberLike): string {
  return hasInvoiceNumber(bill) ? formatInvoiceNumber(bill.invoice_number!) : `bill-${bill.id}`;
}

/** "2025/2026 First Term" — the session and term a bill belongs to, or '' when it has neither. */
export function billPeriodLabel(bill: BillPeriodLike): string {
  return [bill.session?.name, bill.academicCalendar?.termStructure?.name]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');
}
